import React, { useEffect, useState, useRef } from 'react';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Tooltip, Button, notification, Checkbox, Tabs, Form, Col, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CategoryRequest } from '@app/models/product_categories/CategoryRequest';
import {
  storeCategory,
  showCategory,
  editCategory,
  updateCategory,
  createCategory,
} from '@app/api/productCategory.api';
import StatusBar from '@app/components/statusBar/statusBar';
import { notificationController } from '@app/controllers/notificationController';
import { ProductPricingTable } from '../ProductPricing/ProductPricingTable';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';

interface ProductsFormProps {
  pageTitle: string;
  editable: boolean;
  id: string | null;
  showEditIcon?: boolean;
  type: 'edit' | 'create' | 'view';
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const ProductCategoryForm: React.FC<ProductsFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const navigate = useNavigate();
  const categoryId = parseInt(id ?? '0');
  const hasFetchedRef = useRef(false);

  const setApiData = (res: any) => {
    setCurrentBreadcrumName(res?.category?.code || 'New');
    form.setFieldsValue({
      code: res?.category?.code,
      name: res?.category?.name,
      description: res?.category?.description,
      status: res?.category?.status,
      exportable: res?.category?.exportable ?? 'false',
      importable: res?.category?.importable ?? 'false',
    });
    setStatus(res.category.status);
  };

  const setOptions = (res: any) => {
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses);
  };

  const getCategory = () => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    if (categoryId) {
      if (type === 'view') {
        showCategory(categoryId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to category', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editCategory(categoryId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit category', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createCategory()
        .then((res: any) => {
          setOptions(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch categories', description: err.message });
          console.error(err);
        });
    }
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/product-categories')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-product-category`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  useEffect(() => {
    getCategory();
  }, [id, setCurrentBreadcrumName]);

  const handleSubmit = (values: CategoryRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        ...values,
        importable: values.importable ?? false,
        exportable: values.exportable ?? false,
      };

      if (type === 'create') {
        storeCategory(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate('/view-product-category', { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && categoryId) {
        updateCategory(categoryId, submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate('/view-product-pricing', { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <StatusBar id={categoryId} status={status} />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard
            id="productcategoryForm"
            title={pageTitle}
            padding="1.25rem"
            // style={{ margin: '20px', marginTop: '0px', width: '98%' }}
            // extra={
            //   type === 'view' && (
            //     <Tooltip title="Edit">
            //       <Button
            //         type="ghost"
            //         onClick={() => navigate('/edit-product-category', { state: { id: categoryId } })}
            //       >
            //         Edit
            //       </Button>
            //     </Tooltip>
            //   )
            // }
          >
            <BaseRow gutter={[16, 16]}>
              <BaseCol span={24}>
                <Form
                  form={form}
                  name="productcategoryForm"
                  labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                  labelAlign="left"
                  labelWrap
                  wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                  size="small"
                  onFinish={handleSubmit}
                  style={{ marginLeft: '40px' }}
                >
                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item
                        name="code"
                        label="Item Code"
                        rules={[{ required: true, message: 'Item Code is required' }]}
                      >
                        <BaseInput placeholder="Enter Item Code" readOnly={!editable} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={24} lg={12}>
                      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <BaseInput placeholder="Enter Name" readOnly={!editable} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item
                        label="Status"
                        name="status"
                        rules={[{ required: true, message: 'Item Code is required' }]}
                      >
                        {!editable ? (
                          <BaseInput
                            readOnly
                            value={
                              statusOptions.find((option) => option.value === form.getFieldValue('status'))?.label || ''
                            }
                          />
                        ) : (
                          <BaseSelect placeholder="Select Status" disabled={!editable} options={statusOptions} />
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item
                        label="Description"
                        name="description"
                        rules={[{ required: true, message: 'Item Code is required' }]}
                      >
                        {!editable ? (
                          <BaseInput readOnly value={form.getFieldValue('description') || ''} />
                        ) : (
                          <BaseInput.TextArea placeholder="Enter Description" disabled={!editable} />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item name="importable" valuePropName="checked">
                        <Checkbox disabled={!editable}> Importable </Checkbox>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24} lg={12} xl={8}>
                      <Form.Item name="exportable" valuePropName="checked">
                        <Checkbox disabled={!editable}> Exportable </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </BaseCol>
              <BaseCol span={24}>
                {type != 'create' && (
                  <Tabs>
                    <Tabs.TabPane tab="Pricing" key="tab-1">
                      <ProductPricingTable
                        product_category_id={categoryId}
                        product_id={null}
                        product_variety_id={null}
                      />
                    </Tabs.TabPane>
                  </Tabs>
                )}
              </BaseCol>
            </BaseRow>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default ProductCategoryForm;
